import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiZoomIn, FiZoomOut, FiRefreshCw, FiFileText, FiMapPin } from 'react-icons/fi';
import { FaGasPump, FaMapMarkerAlt } from 'react-icons/fa';
import ReactTooltip from 'react-tooltip';
import DropIcon from 'assets/svg/drop.svg';
import CoinsIcon from 'assets/svg/coins.svg';
import { useToasts } from 'react-toast-notifications';
import { VictoryAxis, VictoryChart, VictoryLine, VictoryScatter, VictoryZoomContainer } from 'victory';
import dayjs from 'dayjs';
import DashboardService from 'api/dashboard/DashboardService';
import { FuelTankMarkerDto, FuelTankMarkerStatus, FuelTankTimelineDashboardDto } from 'api/dashboard/dtos/FuelTankTimelineDashboardDto';
import useElementSize from 'common/hooks/useElementSize';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Box from 'common/components/box/Box';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import WidgetNoData from '../components/WidgetNoData';
import openFuelCharges from '../utils/openFuelCharges';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import styles from './FuelTankTimeline.module.scss';

// Cores docs/COLORS.md: teal (linha), verde/vermelho/âmbar (semânticas). Cinzento = neutro (sem base).
const LEVEL_COLOR = '#02c7c7';
// Cores dos pins origem/destino IGUAIS à tab GPS (.sourcePin / .destinationPin no CarGpsTab).
const GPS_ORIGIN_COLOR = '#67BD4C';
const GPS_DEST_COLOR = '#D22222';
const STATUS_COLOR: Record<FuelTankMarkerStatus, string> = {
	COHERENT: '#2dbc6e',
	SUSPECT: '#e04f5f',
	UNRECORDED: '#e8a824',
	UNKNOWN: '#9aa5b1',
};

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
	vehicleId?: string;
	vehicleName?: string;
};

// Popover aberto por clique — numa bola de estado (marker) ou num ponto de trip (trip).
type PopoverTarget =
	| { kind: 'marker'; marker: FuelTankMarkerDto }
	| { kind: 'trip'; tripId: string; date: Date; startLevel: number; endLevel: number | null };
type TooltipState = { x: number; y: number; target: PopoverTarget };

const FuelTankTimeline = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<FuelTankTimelineDashboardDto | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [containerRef, { width, height }] = useElementSize();
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('fuelTankTimeline');
	const scopeVehicleId = useDashboardVehicleScope();
	const vehicleScope = vehicleScopeCriteria(scopeVehicleId);
	const [tooltip, setTooltip] = useState<TooltipState | null>(null);
	// Zoom controlado (para os botões): null = intervalo total; senão [início, fim] em ms.
	const [zoomX, setZoomX] = useState<[number, number] | null>(null);
	const [btnTipId] = useState(() => `ftt-btn-${Math.random().toString(36).slice(2, 8)}`);
	// Séries escondidas via clique na legenda ('level' | 'COHERENT' | 'SUSPECT' | 'UNRECORDED').
	const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
	const toggleSeries = (key: string) => setHiddenSeries(prev => {
		const next = new Set(prev);
		if (next.has(key)) next.delete(key); else next.add(key);
		return next;
	});

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getFuelTankTimeline(criteria);
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	// Ao mudar o período, repor o zoom (o VictoryChart também remonta pela key).
	useEffect(() => { setZoomX(null); }, [dateFrom, dateTo]);

	const levelData = useMemo(() => {
		if (!data?.levels) return [];
		return data.levels.map(l => ({ date: l.date, level: Math.max(0, l.level), tripId: l.tripId }));
	}, [data]);

	const maxLevel = levelData.reduce((max, d) => Math.max(max, d.level), 0);
	// Depósito normalmente 0–100%; o CAN às vezes reporta ~101% (enchem acima do bocal) — dar só
	// uma pequena folga até 105% em vez de saltar para 110%.
	const yMax = maxLevel > 100 ? 105 : 100;
	const yTickValues = [0, 25, 50, 75, 100];

	const markers = data?.markers ?? [];

	const dotData = useMemo(() =>
		markers
			.filter(m => m.level != null)
			.map(m => ({ date: m.date, y: Math.min(Math.max(0, m.level as number), yMax), color: STATUS_COLOR[m.status], marker: m })),
	[markers, yMax]);

	const anomalies = markers.filter(m => m.status === 'SUSPECT' || m.status === 'UNRECORDED').length;

	const hasData = levelData.length > 0 || markers.length > 0;

	// Domínio X total (todas as datas) — base para o zoom controlado e o "repor".
	const fullX = useMemo<[number, number] | null>(() => {
		const times: number[] = [];
		levelData.forEach(d => times.push(d.date.getTime()));
		markers.forEach(m => times.push(m.date.getTime()));
		if (times.length === 0) return null;
		let lo = times[0], hi = times[0];
		for (const tm of times) { if (tm < lo) lo = tm; if (tm > hi) hi = tm; }
		// Começar o eixo no 1.º dia do mês (evita a "barra cortada" a começar no dia 3, etc.).
		lo = dayjs(lo).startOf('month').valueOf();
		if (hi <= lo) hi = lo + 24 * 3600 * 1000;
		return [lo, hi];
	}, [levelData, markers]);

	const effX = zoomX ?? fullX;

	// Marcas do eixo X: garantir sempre o 1.º dia de cada mês (+ dias intermédios conforme o zoom).
	const xTicks = useMemo<Date[] | undefined>(() => {
		if (!effX) return undefined;
		const start = dayjs(effX[0]);
		const end = dayjs(effX[1]);
		const spanDays = end.diff(start, 'day');
		if (spanDays <= 3) return undefined; // zoom muito curto → deixar Victory escolher
		const daysOfMonth = spanDays > 150 ? [1] : spanDays > 75 ? [1, 15] : [1, 8, 15, 22];
		const ticks: Date[] = [];
		let cur = start.startOf('month');
		const guard = end.add(1, 'month');
		while (cur.isBefore(guard)) {
			daysOfMonth.forEach(d => {
				if (d <= cur.daysInMonth()) {
					const tick = cur.date(d);
					if (!tick.isBefore(start) && !tick.isAfter(end)) ticks.push(tick.toDate());
				}
			});
			cur = cur.add(1, 'month');
		}
		return ticks.length ? ticks : undefined;
	}, [effX]);

	const zoomBy = (factor: number) => {
		if (!effX) return;
		const [s, e] = effX;
		const center = (s + e) / 2;
		const half = ((e - s) / 2) * factor;
		let ns = center - half;
		let ne = center + half;
		if (fullX) {
			// Afastar até ao total → voltar ao intervalo completo; não ultrapassar as bordas.
			if (ne - ns >= fullX[1] - fullX[0]) { setZoomX(null); return; }
			if (ns < fullX[0]) { ne += fullX[0] - ns; ns = fullX[0]; }
			if (ne > fullX[1]) { ns = Math.max(fullX[0], ns - (ne - fullX[1])); ne = fullX[1]; }
		}
		setZoomX([ns, ne]);
	};
	const zoomIn = () => zoomBy(0.6);
	const zoomOut = () => zoomBy(1 / 0.6);
	const resetZoom = () => setZoomX(null);

	// Alvo do popover, já com o tipo estreitado (funciona dentro dos onClick).
	const popMarker = tooltip && tooltip.target.kind === 'marker' ? tooltip.target.marker : null;
	const popTrip = tooltip && tooltip.target.kind === 'trip' ? tooltip.target : null;

	const unitFor = (marker: FuelTankMarkerDto) =>
		(marker.fuelName === 'ELECTRIC'
			? t('dashboard.fuel_tank_timeline_unit_kwh' as any)
			: t('dashboard.fuel_tank_timeline_unit_liters' as any)) as string;

	const statusLabel = (status: FuelTankMarkerStatus) =>
		t(`dashboard.fuel_tank_timeline_status_${status.toLowerCase()}` as any) as string;

	const formatNumber = (value: number) => value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

	// Ações do tooltip fixado (nova aba, padrão dos click-through do dashboard).
	const openCharge = (marker: FuelTankMarkerDto) => {
		const vId = scopeVehicleId ?? props.vehicleId;
		if (!vId || !marker.chargeDate) return;
		openFuelCharges({
			dateFrom: dayjs(marker.chargeDate).startOf('day').toDate(),
			dateTo: dayjs(marker.chargeDate).endOf('day').toDate(),
			fuelTypeLabel: t('common.generic_types.FUEL' as any) as string,
			vehicle: { id: vId, name: props.vehicleName ?? '' },
		});
	};

	// GPS do ativo num dia concreto, já na viagem certa (tripId → a GPS tab seleciona/scrolla).
	const openGpsForDate = (date: Date, tripId?: string | null) => {
		const vId = scopeVehicleId ?? props.vehicleId;
		if (!vId) return;
		const day = dayjs(date).format('YYYY-MM-DD');
		window.open(`/vehicles/trip/details/${vId}/gps/list/${day}/${tripId || 'none'}/none`, '_blank');
	};
	const openGps = (marker: FuelTankMarkerDto) => openGpsForDate(marker.date, marker.tripId);

	// Clique na linha: converte a posição em data (via escala do Victory) e escolhe a última
	// leitura ≤ essa data — assim um trecho parado herda a viagem que o originou (o ponto anterior).
	const handleLineClick = (evt: any, targetProps: any) => {
		if (!levelData.length || !targetProps?.scale?.x) return;
		if (evt.stopPropagation) evt.stopPropagation();
		const svgEl = evt.target?.ownerSVGElement;
		if (!svgEl) return;
		const rect = svgEl.getBoundingClientRect();
		const px = rect.width ? (evt.clientX - rect.left) * (width / rect.width) : (evt.clientX - rect.left);
		const dataX = Number(targetProps.scale.x.invert(px));
		let chosen = levelData[0];
		for (const d of levelData) {
			if (d.date.getTime() <= dataX) chosen = d; else break;
		}
		// Trajeto tem 2 leituras no levelData (início e fim, mesmo tripId) → mostrar ambas.
		const tripReadings = levelData.filter(d => d.tripId === chosen.tripId).sort((a, b) => a.date.getTime() - b.date.getTime());
		const startR = tripReadings[0] ?? chosen;
		const endR = tripReadings.length > 1 ? tripReadings[tripReadings.length - 1] : null;
		setTooltip({ x: evt.clientX, y: evt.clientY, target: { kind: 'trip', tripId: chosen.tripId, date: startR.date, startLevel: startR.level, endLevel: endR ? endR.level : null } });
	};

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.fuel_tank_timeline' as any) as string}
			subTitleTooltip={t('dashboard.fuel_tank_timeline_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			<KpiHeader
				value={`${anomalies}`}
				subLabel={t('dashboard.fuel_tank_timeline_anomalies' as any) as string}
				currentTotal={anomalies}
				previousTotal={null}
				previousRange={null}
			/>
			<div className={styles.legendRow}>
				<div className={styles.legend}>
					<span className={`${styles.legendItem} ${hiddenSeries.has('level') ? styles.legendHidden : ''}`} onClick={() => toggleSeries('level')}>
						<span className={styles.legendLine} style={{ backgroundColor: LEVEL_COLOR }} />
						{t('dashboard.fuel_tank_timeline_level' as any) as string}
					</span>
					<span className={`${styles.legendItem} ${hiddenSeries.has('COHERENT') ? styles.legendHidden : ''}`} onClick={() => toggleSeries('COHERENT')}>
						<span className={styles.legendDot} style={{ backgroundColor: STATUS_COLOR.COHERENT }} />
						{statusLabel('COHERENT')}
					</span>
					<span className={`${styles.legendItem} ${hiddenSeries.has('SUSPECT') ? styles.legendHidden : ''}`} onClick={() => toggleSeries('SUSPECT')}>
						<span className={styles.legendDot} style={{ backgroundColor: STATUS_COLOR.SUSPECT }} />
						{statusLabel('SUSPECT')}
					</span>
					<span className={`${styles.legendItem} ${hiddenSeries.has('UNRECORDED') ? styles.legendHidden : ''}`} onClick={() => toggleSeries('UNRECORDED')}>
						<span className={styles.legendDot} style={{ backgroundColor: STATUS_COLOR.UNRECORDED }} />
						{statusLabel('UNRECORDED')}
					</span>
				</div>
				<div className={styles.zoomControls}>
					<button type="button" className={styles.zoomButton} title={t('dashboard.fuel_tank_timeline_zoom_in' as any) as string} aria-label={t('dashboard.fuel_tank_timeline_zoom_in' as any) as string} onClick={zoomIn}>
						<FiZoomIn />
					</button>
					<button type="button" className={styles.zoomButton} title={t('dashboard.fuel_tank_timeline_zoom_out' as any) as string} aria-label={t('dashboard.fuel_tank_timeline_zoom_out' as any) as string} onClick={zoomOut}>
						<FiZoomOut />
					</button>
					<button type="button" className={styles.zoomButton} title={t('dashboard.fuel_tank_timeline_reset_zoom' as any) as string} aria-label={t('dashboard.fuel_tank_timeline_reset_zoom' as any) as string} onClick={resetZoom}>
						<FiRefreshCw />
					</button>
				</div>
			</div>
			<div className={styles.chartContainer} ref={containerRef}>
				{isLoading ? (
					<div className={styles.centerAlign}><LoadingSpinner /></div>
				) : !hasData ? (
					<WidgetNoData />
				) : (
					width > 0 && height > 0 && (
						<VictoryChart
							// Remonta ao mudar o período → o zoom/pan volta ao início (não em resize).
							key={`${dateFrom.getTime()}-${dateTo.getTime()}`}
							width={width}
							height={height}
							scale={{ x: 'time' }}
							domain={{ y: [0, yMax] }}
							domainPadding={{ x: 16, y: 0 }}
							padding={{ top: 16, bottom: 40, left: 46, right: 16 }}
							containerComponent={
								<VictoryZoomContainer
									zoomDimension="x"
									minimumZoom={{ x: 6 * 3600 * 1000 }}
									zoomDomain={effX ? { x: [new Date(effX[0]), new Date(effX[1])] } : undefined}
									onZoomDomainChange={d => {
										if (!d || !d.x) return;
										let a = Number(d.x[0]);
										let b = Number(d.x[1]);
										if (!isFinite(a) || !isFinite(b)) return;
										if (a > b) { const tmp = a; a = b; b = tmp; }
										// Nao deixar o zoom/pan sair do intervalo com dados (evita saltos p/ zona vazia).
										if (fullX) {
											const span = Math.min(b - a, fullX[1] - fullX[0]);
											if (a < fullX[0]) { a = fullX[0]; b = a + span; }
											if (b > fullX[1]) { b = fullX[1]; a = b - span; }
										}
										setZoomX([a, b]);
									}}
								/>
							}
						>
							<VictoryAxis
								scale="time"
								tickValues={xTicks}
								tickCount={7}
								fixLabelOverlap
								style={{
									axis: { stroke: '#e5e7eb' },
									tickLabels: { fill: '#6b7280', userSelect: 'none', fontSize: 11, fontFamily: 'Roboto' },
								}}
								tickFormat={x => dayjs(x).format((effX && (effX[1] - effX[0]) <= 2 * 86400000) ? 'DD/MM HH:mm' : 'DD/MM')}
							/>
							<VictoryAxis
								dependentAxis
								tickValues={yTickValues}
								style={{
									axis: { stroke: 'transparent' },
									grid: { stroke: '#f5f5f5' },
									tickLabels: { fontSize: 10, fontFamily: 'Roboto', fill: '#6b7280' },
								}}
								tickFormat={x => `${x}%`}
							/>

							{/* Só o estado indeterminado (sem bola) mostra uma linha vertical tracejada de
							     altura total. As bolas não têm linha vertical por cima. */}
							{markers.filter(m => m.level == null).map((m, i) => (
								<VictoryLine
									key={`vline-${i}`}
									data={[{ date: m.date, y: 0 }, { date: m.date, y: yMax }]}
									x="date"
									y="y"
									style={{ data: { stroke: STATUS_COLOR[m.status], strokeWidth: 1.5, strokeDasharray: '4,4' } }}
								/>
							))}

							{/* Linha do nível do depósito (CAN). */}
							{levelData.length > 0 && !hiddenSeries.has('level') && (
								<VictoryLine
									data={levelData}
									x="date"
									y="level"
									interpolation="monotoneX"
									style={{ data: { stroke: LEVEL_COLOR, strokeWidth: 3 } }}
								/>
							)}

							{/* Linha invisível grossa por cima da visível → toda a linha é clicável (sem
							     pontos). O clique calcula a data e escolhe a última leitura ≤ essa data
							     (assume a viagem anterior nos trechos parados). */}
							{levelData.length > 0 && !hiddenSeries.has('level') && (
								<VictoryLine
									data={levelData}
									x="date"
									y="level"
									interpolation="monotoneX"
									style={{ data: { stroke: 'transparent', strokeWidth: 20, pointerEvents: 'all' } }}
									// eslint-disable-next-line
									events={[{
										target: 'data' as const,
										eventHandlers: {
											onClick: (evt: any, targetProps: any) => { handleLineClick(evt, targetProps); return []; },
										},
									}]}
								/>
							)}

							{/* Bolas dos abastecimentos, cor por estado. */}
							<VictoryScatter
								data={dotData.filter(d => !hiddenSeries.has(d.marker.status))}
								x="date"
								y="y"
								size={5}
								style={{ data: { fill: ({ datum }: any) => datum.color, stroke: '#fff', strokeWidth: 1.5, cursor: 'pointer' } }}
								// eslint-disable-next-line
								events={[{
									target: 'data' as const,
									eventHandlers: {
										onClick: (evt: any, targetProps: any) => {
											if (evt.stopPropagation) evt.stopPropagation();
											setTooltip({ x: evt.clientX, y: evt.clientY, target: { kind: 'marker', marker: targetProps.datum.marker } });
											return [];
										},
									},
								}]}
							/>
						</VictoryChart>
					)
				)}
					{tooltip && (
						<>
						{createPortal(
							<div className={styles.popoverBackdrop} onMouseDown={() => setTooltip(null)} />,
							document.body,
						)}
						<ChartTooltipPortal clientX={tooltip.x} clientY={tooltip.y} className={`${styles.tooltip} ${styles.tooltipPinned}`}>
							{popMarker && (
								<>
									<div className={styles.tooltipTitle}>{dayjs(popMarker.chargeDate ?? popMarker.date).format('DD/MM/YYYY')}</div>
									<div className={styles.tooltipRow}>
										<span className={styles.tooltipDot} style={{ backgroundColor: STATUS_COLOR[popMarker.status] }} />
										<span className={styles.tooltipRowValue}>{statusLabel(popMarker.status)}</span>
									</div>
									{popMarker.quantity != null && (
										<div className={styles.tooltipRow}>
											<span className={styles.tooltipRowLabel} data-tip={t('dashboard.fuel_tank_timeline_quantity' as any) as string} data-for={btnTipId}><img src={DropIcon} className={styles.tooltipRowImg} alt="" /></span>
											<span className={styles.tooltipRowValue}>{formatNumber(popMarker.quantity)} {unitFor(popMarker)}</span>
										</div>
									)}
									{popMarker.cost != null && (
										<div className={styles.tooltipRow}>
											<span className={styles.tooltipRowLabel} data-tip={t('dashboard.fuel_tank_timeline_cost' as any) as string} data-for={btnTipId}><img src={CoinsIcon} className={styles.tooltipRowImg} alt="" /></span>
											<span className={styles.tooltipRowValue}>{formatNumber(popMarker.cost)} €</span>
										</div>
									)}
									{popMarker.observedRise != null && popMarker.level != null && (
										<div className={styles.tooltipRow}>
											<span className={styles.tooltipFuelIcon} data-tip={t('dashboard.fuel_tank_timeline_rise' as any) as string} data-for={btnTipId}><FaGasPump />:</span>
												<span className={styles.tooltipTripLevels}>
													{Math.round(popMarker.level)}% <FaMapMarkerAlt style={{ color: GPS_ORIGIN_COLOR }} />
													{' → '}{Math.round(popMarker.level + popMarker.observedRise)}% <FaMapMarkerAlt style={{ color: GPS_DEST_COLOR }} />
												</span>
										</div>
									)}
									<div className={styles.tooltipActions}>
										{popMarker.chargeDate && (
											<button type="button" className={styles.tooltipIconButton} data-tip={t('dashboard.fuel_tank_timeline_open_charge' as any) as string} data-for={btnTipId} onClick={() => { openCharge(popMarker); setTooltip(null); }}><FiFileText /></button>
										)}
										<button type="button" className={styles.tooltipIconButton} data-tip={t('dashboard.fuel_tank_timeline_open_gps' as any) as string} data-for={btnTipId} onClick={() => { openGps(popMarker); setTooltip(null); }}><FiMapPin /></button>
									</div>
								</>
							)}
							{popTrip && (
								<>
									<div className={styles.tooltipTitle}>{dayjs(popTrip.date).format('DD/MM/YYYY HH:mm')}</div>
									<div className={styles.tooltipRow}>
										<span className={styles.tooltipFuelIcon} data-tip={t('dashboard.fuel_tank_timeline_tank_level' as any) as string} data-for={btnTipId}><FaGasPump />:</span>
										<span className={styles.tooltipTripLevels}>
												{Math.round(popTrip.startLevel)}%
												<FaMapMarkerAlt style={{ color: GPS_ORIGIN_COLOR }} />
												{popTrip.endLevel != null && (
													<>{' → '}{Math.round(popTrip.endLevel)}% <FaMapMarkerAlt style={{ color: GPS_DEST_COLOR }} /></>
												)}
											</span>
									</div>
									<div className={styles.tooltipActions}>
										<button type="button" className={styles.tooltipIconButton} data-tip={t('dashboard.fuel_tank_timeline_open_gps' as any) as string} data-for={btnTipId} onClick={() => { openGpsForDate(popTrip.date, popTrip.tripId); setTooltip(null); }}><FiMapPin /></button>
									</div>
								</>
							)}
							<ReactTooltip id={btnTipId} effect="solid" place="top" />
						</ChartTooltipPortal>
						</>
					)}
			</div>
		</Box>
	);
};

export default FuelTankTimeline;
